<script lang="js">
import axios from "axios";
import Swal from "sweetalert2";
import LegacyStudentImportModal from "./LegacyStudentImportModal.vue";

export default {
  name: "UtilitiesMgt",
  components: { LegacyStudentImportModal },
  data() {
    return {
      logger: null,
      utilities: [],
      loading: false,
      runningUtilityKey: "",
    };
  },
  created() {
    this.logger = this.$createLogger("utilitiesMgt");
  },
  computed: {
    canRunUtilities() {
      return this.$store.getters.hasPermission("utilities", "manage");
    },
  },
  async mounted() {
    await this.fetchUtilities();
  },
  methods: {
    async fetchUtilities() {
      this.loading = true;

      try {
        const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/utilities`);
        this.utilities = response.data?.data?.utilities || [];
      } catch (error) {
        this.logger.error("Error fetching utilities:", error);
        Swal.fire("Error", "Failed to load utilities.", "error");
      } finally {
        this.loading = false;
      }
    },

    async confirmRunUtility(utility, dryRun = false) {
      if (!this.canRunUtilities || this.runningUtilityKey) return;
      if (utility.supportsCleanup && !dryRun && !utility.normalizeReady) {
        await Swal.fire(
          "Dry run required",
          "Complete a successful dry run before normalising application document storage.",
          "info"
        );
        return;
      }
      const actionLabel = dryRun ? "Dry Run" : "Run Utility";
      const inputFields = Array.isArray(utility.inputFields)
        ? utility.inputFields
        : [];

      const result = await Swal.fire({
        title: actionLabel,
        html: `
          <div class="text-start">
            <p class="mb-2"><strong>${this.escapeHtml(utility.name)}</strong></p>
            <p class="text-muted mb-3">${this.escapeHtml(utility.description)}</p>
            ${this.buildUtilityInputFieldsHtml(inputFields)}
            <div class="alert ${dryRun ? "alert-info" : "alert-warning"} mb-0">
              ${dryRun
                ? this.escapeHtml(
                    utility.dryRunNotice ||
                      "This will simulate the utility and return the impact summary without applying updates."
                  )
                : this.escapeHtml(
                    utility.executionNotice ||
                      "This will execute the utility immediately and write changes to the database."
                  )}
            </div>
          </div>
        `,
        icon: dryRun ? "info" : "warning",
        showCancelButton: true,
        confirmButtonText: actionLabel,
        cancelButtonText: "Cancel",
        focusConfirm: false,
        preConfirm: () => {
          const params = {};

          inputFields.forEach((field) => {
            const element = document.getElementById(`utility-input-${field.key}`);
            params[field.key] = element ? String(element.value || "").trim() : "";
          });

          const requiredField = inputFields.find(
            (field) => field.required && !params[field.key]
          );

          if (requiredField) {
            Swal.showValidationMessage(`${requiredField.label} is required`);
            return false;
          }

          return params;
        },
      });

      if (!result.isConfirmed) {
        return;
      }

      await this.runUtility(utility, dryRun, result.value || {});
    },

    async confirmCleanupUtility(utility) {
      if (!this.canRunUtilities || this.runningUtilityKey) return;
      if (!utility.cleanupReady) {
        Swal.fire(
          "Cleanup unavailable",
          "Run and verify a successful normalisation before deleting retained legacy objects and aged orphans.",
          "info"
        );
        return;
      }
      const phrase = utility.cleanupConfirmation;
      const result = await Swal.fire({
        title: "Delete legacy objects and orphans",
        icon: "warning",
        html: `
          <div class="text-start">
            <p>This permanently deletes verified retained source objects and unreferenced application objects older than the safety period.</p>
            <div class="alert alert-danger">
              Canonical referenced objects, recent objects and objects outside approved application prefixes are protected.
            </div>
            <label class="form-label" for="storage-cleanup-confirmation">
              Enter <strong>${this.escapeHtml(phrase)}</strong> to continue
            </label>
            <input id="storage-cleanup-confirmation" class="form-control" autocomplete="off" />
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: "Delete verified objects",
        confirmButtonColor: "#dc3545",
        focusConfirm: false,
        preConfirm: () => {
          const confirmation = String(
            document.getElementById("storage-cleanup-confirmation")?.value || ""
          ).trim();
          if (confirmation !== phrase) {
            Swal.showValidationMessage("The confirmation phrase does not match");
            return false;
          }
          return confirmation;
        },
      });
      if (!result.isConfirmed) return;
      await this.runUtility(utility, false, {
        action: "cleanup",
        confirmation: result.value,
      });
    },

    openUtility(utility) {
      if (!utility.canRun) {
        Swal.fire("Permission denied", "You cannot run this utility.", "error");
        return;
      }
      if (utility.workflow === "legacy-student-import") {
        this.$refs.legacyStudentImport?.open();
      }
    },

    async handleLegacyStudentImported() {
      await this.fetchUtilities();
    },

    async runUtility(utility, dryRun = false, params = {}) {
      const operation = dryRun
        ? "dry"
        : params.action === "cleanup"
        ? "cleanup"
        : "run";
      this.runningUtilityKey = `${utility.key}:${operation}`;

      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/utilities/${utility.key}/run`,
          { dryRun, params }
        );

        const run = response.data?.data?.run;
        await Swal.fire({
          title: dryRun
            ? "Dry run complete"
            : operation === "cleanup"
            ? "Cleanup completed"
            : "Utility completed",
          html: `
            <div class="text-start">
              <p class="mb-2"><strong>${this.escapeHtml(utility.name)}</strong></p>
              <pre class="small bg-light border rounded p-3 mb-0">${this.escapeHtml(
                JSON.stringify(run?.summary || {}, null, 2)
              )}</pre>
            </div>
          `,
          icon: "success",
          width: 720,
        });
        await this.fetchUtilities();
      } catch (error) {
        this.logger.error(`Error running utility ${utility.key}:`, error);
        Swal.fire(
          "Error",
          error.response?.data?.message || "Failed to run utility.",
          "error"
        );
      } finally {
        this.runningUtilityKey = "";
      }
    },

    async viewUtilityDetails(utility, page = 1) {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/utilities/${utility.key}`,
          {
            params: {
              page,
              limit: 5,
            },
          }
        );
        const payload = response.data?.data || {};
        const lastRun = payload.lastRun;
        const history = Array.isArray(payload.history) ? payload.history : [];
        const pagination = payload.pagination || {
          currentPage: 1,
          totalPages: 1,
          hasPreviousPage: false,
          hasNextPage: false,
        };

        await Swal.fire({
          title: this.escapeHtml(utility.name),
          width: 900,
          showConfirmButton: false,
          showCloseButton: true,
          html: this.buildUtilityDetailsHtml(
            payload.utility || utility,
            lastRun,
            history,
            pagination
          ),
          didOpen: () => {
            const htmlContainer = Swal.getHtmlContainer();
            if (htmlContainer) {
              htmlContainer.style.maxHeight = "70vh";
              htmlContainer.style.overflowY = "auto";
            }

            const previousButton = document.getElementById("utilityHistoryPreviousBtn");
            const nextButton = document.getElementById("utilityHistoryNextBtn");

            if (previousButton && pagination.hasPreviousPage) {
              previousButton.addEventListener("click", () => {
                this.viewUtilityDetails(utility, pagination.currentPage - 1);
              });
            }

            if (nextButton && pagination.hasNextPage) {
              nextButton.addEventListener("click", () => {
                this.viewUtilityDetails(utility, pagination.currentPage + 1);
              });
            }
          },
        });
      } catch (error) {
        this.logger.error(`Error fetching utility details for ${utility.key}:`, error);
        Swal.fire(
          "Error",
          error.response?.data?.message || "Failed to load utility details.",
          "error"
        );
      }
    },

    buildUtilityDetailsHtml(utility, lastRun, history, pagination) {
      const latestSummary = lastRun?.summary
        ? `<pre class="small bg-light border rounded p-3 mb-0">${this.escapeHtml(
            JSON.stringify(lastRun.summary, null, 2)
          )}</pre>`
        : '<p class="text-muted mb-0">No summary available.</p>';

      const latestStartedBy = this.getStartedByLabel(lastRun);

      const historyRows = history.length
        ? history
            .map(
              (run) => `
                <tr>
                  <td>${this.escapeHtml(this.formatDateTime(run.startedAt))}</td>
                  <td>${this.escapeHtml(this.getStartedByLabel(run))}</td>
                  <td>${this.escapeHtml(this.formatLabel(run.status || "not-run"))}</td>
                  <td>${this.escapeHtml(
                    run.dryRun
                      ? "Dry Run"
                      : run.input?.action === "cleanup"
                      ? "Cleanup"
                      : "Run"
                  )}</td>
                  <td>${this.escapeHtml(this.formatDateTime(run.finishedAt))}</td>
                  <td>${this.escapeHtml(run.error || "-")}</td>
                </tr>
              `
            )
            .join("")
        : '<tr><td colspan="6" class="text-center text-muted py-3">No run history available.</td></tr>';

      return `
        <div class="text-start utility-detail-modal">
          <div class="mb-4">
            <h6 class="fw-bold mb-2">Description</h6>
            <p class="text-muted mb-0">${this.escapeHtml(utility.description || "No description available.")}</p>
          </div>

          <div class="row g-3 mb-4">
            <div class="col-md-4">
              <div class="border rounded-3 p-3 h-100 bg-light-subtle">
                <div class="small text-muted mb-1">Category</div>
                <div class="fw-semibold">${this.escapeHtml(utility.category || "N/A")}</div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="border rounded-3 p-3 h-100 bg-light-subtle">
                <div class="small text-muted mb-1">Latest Run</div>
                <div class="fw-semibold">${this.escapeHtml(this.formatDateTime(lastRun?.startedAt))}</div>
                <div class="small text-muted mt-1">By ${this.escapeHtml(latestStartedBy)}</div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="border rounded-3 p-3 h-100 bg-light-subtle">
                <div class="small text-muted mb-1">Latest Status</div>
                <div class="fw-semibold">${this.escapeHtml(this.formatLabel(lastRun?.status || "not-run"))}</div>
              </div>
            </div>
          </div>

          <div class="mb-4">
            <h6 class="fw-bold mb-2">Latest Summary</h6>
            ${latestSummary}
          </div>

          <div>
            <h6 class="fw-bold mb-2">Run History</h6>
            <div class="table-responsive border rounded-3">
              <table class="table table-sm align-middle mb-0">
                <thead class="table-light">
                  <tr>
                    <th>Started</th>
                    <th>Started By</th>
                    <th>Status</th>
                    <th>Mode</th>
                    <th>Finished</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>${historyRows}</tbody>
              </table>
            </div>
            <div class="d-flex justify-content-between align-items-center pt-3">
              <div class="small text-muted">
                Page ${this.escapeHtml(String(pagination.currentPage || 1))} of ${this.escapeHtml(
                  String(pagination.totalPages || 1)
                )}
              </div>
              <div class="d-flex gap-2">
                <button
                  id="utilityHistoryPreviousBtn"
                  type="button"
                  class="btn btn-outline-secondary btn-sm ${pagination.hasPreviousPage ? "" : "disabled"}"
                >
                  Previous
                </button>
                <button
                  id="utilityHistoryNextBtn"
                  type="button"
                  class="btn btn-outline-secondary btn-sm ${pagination.hasNextPage ? "" : "disabled"}"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      `;
    },

    formatDateTime(value) {
      if (!value) {
        return "Never";
      }

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return "Invalid date";
      }

      return date.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    formatLabel(value = "") {
      return String(value)
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/[_-]/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
    },

    getStartedByLabel(run) {
      if (!run) {
        return "Unknown staff";
      }

      if (run.startedByName && run.startedByStaffNumber) {
        return `${run.startedByName} (${run.startedByStaffNumber})`;
      }

      if (run.startedByName) {
        return run.startedByName;
      }

      const firstName = run.startedBy?.userId?.firstName || "";
      const lastName = run.startedBy?.userId?.lastName || "";
      const staffNumber = run.startedBy?.staffNumber || run.startedByStaffNumber || "";
      const fullName = `${firstName} ${lastName}`.trim();

      if (fullName && staffNumber) {
        return `${fullName} (${staffNumber})`;
      }

      if (fullName) {
        return fullName;
      }

      return run.startedByStaffNumber || "Unknown staff";
    },

    getRunStatusBadgeClass(status) {
      return {
        running: "bg-warning-subtle text-warning-emphasis",
        successful: "bg-success-subtle text-success-emphasis",
        failed: "bg-danger-subtle text-danger-emphasis",
      }[String(status || "").toLowerCase()] || "bg-secondary-subtle text-secondary-emphasis";
    },

    buildUtilityInputFieldsHtml(fields = []) {
      if (!fields.length) {
        return "";
      }

      const inputs = fields
        .map(
          (field) => `
            <div class="mb-3">
              <label class="form-label mb-1" for="utility-input-${this.escapeHtml(field.key)}">
                ${this.escapeHtml(field.label || field.key)}${field.required ? " *" : ""}
              </label>
              <input
                id="utility-input-${this.escapeHtml(field.key)}"
                class="form-control"
                type="text"
                placeholder="${this.escapeHtml(field.placeholder || "")}" />
              ${field.helpText
                ? `<div class="form-text">${this.escapeHtml(field.helpText)}</div>`
                : ""
              }
            </div>
          `
        )
        .join("");

      return `
        <div class="border rounded p-3 mb-3 bg-light-subtle">
          <h6 class="fw-semibold mb-3">Inputs</h6>
          ${inputs}
        </div>
      `;
    },

    escapeHtml(value) {
      return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    },
  },
};
</script>

<template>
  <div class="p-4 text-start utilities-mgt">
    <div
      class="d-flex flex-column flex-lg-row justify-content-between gap-3 mb-4"
    >
      <div>
        <h4 class="fw-bold mb-1">Utilities</h4>
        <p class="text-muted mb-0">
          Run controlled maintenance and readiness utilities from the staff
          portal.
        </p>
      </div>
      <div>
        <button
          class="btn btn-outline-secondary btn-sm"
          @click="fetchUtilities"
          :disabled="loading"
        >
          <span
            v-if="loading"
            class="spinner-border spinner-border-sm me-1"
            role="status"
          ></span>
          <i v-else class="bi bi-arrow-clockwise me-1"></i>
          Refresh
        </button>
      </div>
    </div>

    <div class="card border-0 shadow-sm">
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table align-middle mb-0">
            <thead class="table-light">
              <tr>
                <th class="ps-4">Utility</th>
                <th>Description</th>
                <th>Category</th>
                <th>Last Run</th>
                <th>Status</th>
                <th class="text-end pe-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading">
                <td colspan="6" class="text-center py-5">
                  <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                </td>
              </tr>
              <tr v-else-if="!utilities.length">
                <td colspan="6" class="text-center py-5 text-muted">
                  No utilities are available.
                </td>
              </tr>
              <tr v-for="utility in utilities" :key="utility.key">
                <td class="ps-4">
                  <div class="fw-semibold">{{ utility.name }}</div>
                  <div class="small text-muted">{{ utility.key }}</div>
                </td>
                <td>
                  <div
                    class="text-muted small text-break utility-description mb-0"
                  >
                    {{ utility.description }}
                  </div>
                </td>
                <td>
                  <span class="badge bg-primary-subtle text-primary-emphasis">
                    {{ utility.category }}
                  </span>
                </td>
                <td>
                  <div>{{ formatDateTime(utility.lastRun?.startedAt) }}</div>
                  <div v-if="utility.lastRun?.dryRun" class="small text-muted">
                    Dry run
                  </div>
                </td>
                <td>
                  <span
                    class="badge"
                    :class="getRunStatusBadgeClass(utility.lastRun?.status)"
                  >
                    {{ formatLabel(utility.lastRun?.status || "not-run") }}
                  </span>
                </td>
                <td class="text-center pe-4">
                  <div class="dropdown">
                    <a
                      class="dropdown-toggle no-caret text-decoration-none text-dark fs-5"
                      href="#"
                      role="button"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      <i class="bi bi-three-dots-vertical"></i>
                    </a>

                    <ul class="dropdown-menu">
                      <li>
                        <a
                          class="dropdown-item text-primary-emphasis"
                          href="#"
                          @click.prevent="viewUtilityDetails(utility)"
                        >
                          <i class="bi bi-eye-fill"></i> View details
                        </a>
                      </li>
                      <li v-if="utility.workflow === 'legacy-student-import'">
                        <a
                          class="dropdown-item text-success"
                          :class="{ disabled: !utility.canRun }"
                          href="#"
                          @click.prevent="openUtility(utility)"
                        >
                          <i class="bi bi-person-plus-fill"></i> Open importer
                        </a>
                      </li>
                      <li v-if="!utility.workflow">
                        <a
                          class="dropdown-item text-info-emphasis"
                          :class="{
                            disabled:
                              !canRunUtilities || Boolean(runningUtilityKey),
                          }"
                          :aria-disabled="
                            !canRunUtilities || Boolean(runningUtilityKey)
                          "
                          href="#"
                          @click.prevent="confirmRunUtility(utility, true)"
                        >
                          <span
                            v-if="runningUtilityKey === `${utility.key}:dry`"
                            class="spinner-border spinner-border-sm me-1"
                            role="status"
                          ></span>
                          <i class="bi bi-fast-forward-circle-fill"></i> Dry Run
                        </a>
                      </li>
                      <li v-if="!utility.workflow">
                        <a
                          class="dropdown-item text-success"
                          :class="{
                            disabled:
                              !canRunUtilities ||
                              (utility.supportsCleanup &&
                                !utility.normalizeReady) ||
                              Boolean(runningUtilityKey),
                          }"
                          :aria-disabled="
                            !canRunUtilities ||
                            (utility.supportsCleanup &&
                              !utility.normalizeReady) ||
                            Boolean(runningUtilityKey)
                          "
                          href="#"
                          @click.prevent="confirmRunUtility(utility)"
                        >
                          <span
                            v-if="runningUtilityKey === `${utility.key}:run`"
                            class="spinner-border spinner-border-sm me-1"
                            role="status"
                          ></span>
                          <i class="bi bi-play-circle-fill"></i> Run
                        </a>
                      </li>
                      <li v-if="utility.supportsCleanup">
                        <a
                          class="dropdown-item text-danger"
                          :class="{
                            disabled:
                              !canRunUtilities ||
                              !utility.cleanupReady ||
                              Boolean(runningUtilityKey),
                          }"
                          :aria-disabled="
                            !canRunUtilities ||
                            !utility.cleanupReady ||
                            Boolean(runningUtilityKey)
                          "
                          href="#"
                          @click.prevent="confirmCleanupUtility(utility)"
                        >
                          <span
                            v-if="
                              runningUtilityKey === `${utility.key}:cleanup`
                            "
                            class="spinner-border spinner-border-sm me-1"
                            role="status"
                          ></span>
                          <i v-else class="bi bi-trash3-fill"></i>
                          Delete legacy &amp; orphans
                        </a>
                      </li>
                    </ul>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <LegacyStudentImportModal
      ref="legacyStudentImport"
      @imported="handleLegacyStudentImported"
    />
  </div>
</template>

<style scoped>
.utilities-mgt .table td,
.utilities-mgt .table th {
  vertical-align: middle;
}

.utility-description {
  max-width: 320px;
  white-space: normal;
}
</style>
